import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, ArrowLeft, Calendar, Camera, Dog, Users, Globe } from 'lucide-react';
import axios from 'axios';
import SportPhotoGrid from '../components/sport/SportPhotoGrid';
import SportGalleryToolbar from '../components/sport/SportGalleryToolbar';
import SportLightbox from '../components/sport/SportLightbox';
import useFavorites from '../hooks/useFavorites';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SportDogPage() {
  const { dogId } = useParams();
  const { language, setLanguage } = useLanguage();
  const [dog, setDog] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Gallery state
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('date_desc');
  const [filterRatings, setFilterRatings] = useState([]);
  const [filenameSearch, setFilenameSearch] = useState('');
  const [pageSize, setPageSize] = useState(50);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Favorites
  const { favoriteIds, toggleFavorite } = useFavorites();

  useEffect(() => {
    const fetchDog = async () => {
      try {
        const res = await axios.get(`${API}/sport/dogs/${dogId}`);
        setDog(res.data);
      } catch (err) {
        console.error('Failed to load dog:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDog();
  }, [dogId]);

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const res = await axios.get(`${API}/sport/public/dogs/${dogId}/photos?page=${page}&limit=${pageSize}`);
        setPhotos(res.data.photos);
        setTotal(res.data.total);
      } catch (err) {
        console.error('Failed to load dog photos:', err);
      }
    };
    fetchPhotos();
  }, [dogId, page, pageSize]);

  const t = language === 'pl'
    ? { photos: 'zdjec', noPhotos: 'Brak zdjec', prev: 'Poprzednia', next: 'Nastepna', addToCart: 'Do koszyka', inCart: 'W koszyku' }
    : { photos: 'photos', noPhotos: 'No photos', prev: 'Previous', next: 'Next', addToCart: 'Add', inCart: 'In cart' };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!dog) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">
          {language === 'pl' ? 'Pies nie znaleziony' : 'Dog not found'}
        </p>
      </div>
    );
  }

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => window.history.back()} className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <Dog className="w-6 h-6" />
            <h1 className="text-xl font-bold">{dog.name || `#${dog.id.slice(0, 8)}`}</h1>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLanguage(language === 'pl' ? 'en' : 'pl')}
          >
            <Globe className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Dog Profile */}
      <section className="px-6 py-8 border-b border-border">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-start gap-6"
          >
            <div className="w-24 h-24 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
              <Dog className="w-12 h-12 text-muted-foreground" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">{dog.name || (language === 'pl' ? 'Nieznany' : 'Unknown')}</h2>
              {dog.breed && <p className="text-muted-foreground">{dog.breed}</p>}
              {dog.color && <p className="text-sm text-muted-foreground">{dog.color}</p>}
              {dog.team && (
                <Link
                  to={`/sport/team/${dog.team.id}`}
                  className="flex items-center gap-1 text-sm text-primary hover:underline mt-2"
                >
                  <Users className="w-3 h-3" /> {dog.team.name}
                  {dog.team.club && <span className="text-muted-foreground"> ({dog.team.club})</span>}
                </Link>
              )}
              <div className="flex gap-4 mt-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Camera className="w-3 h-3" /> {dog.photo_count} {t.photos}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> {(dog.events || []).length} {language === 'pl' ? 'zawodow' : 'events'}
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Events this dog appeared in */}
      {dog.events && dog.events.length > 0 && (
        <section className="px-6 py-6 border-b border-border">
          <div className="max-w-6xl mx-auto">
            <h3 className="text-lg font-semibold mb-3">
              {language === 'pl' ? 'Zawody' : 'Events'}
            </h3>
            <div className="flex flex-wrap gap-2">
              {dog.events.map(event => (
                <Link key={event.id} to={`/sport/event/${event.id}?tab=photos&dog=${dogId}`}>
                  <Button variant="outline" size="sm">
                    {event.name}
                    <span className="ml-1 text-xs opacity-60">
                      {event.date_start ? new Date(event.date_start).toLocaleDateString(language === 'pl' ? 'pl-PL' : 'en-US', { month: 'short', day: 'numeric' }) : ''}
                    </span>
                  </Button>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Photos */}
      <section className="px-6 py-8">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-lg font-semibold mb-4">
            {language === 'pl' ? 'Zdjecia' : 'Photos'} ({total})
          </h3>

          <SportGalleryToolbar
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            sortBy={sortBy}
            onSortChange={(v) => { setSortBy(v); setPage(1); }}
            filterRatings={filterRatings}
            onRatingsChange={(v) => { setFilterRatings(v); setPage(1); }}
            filenameSearch={filenameSearch}
            onFilenameSearchChange={(v) => { setFilenameSearch(v); setPage(1); }}
            pageSize={pageSize}
            onPageSizeChange={(v) => { setPageSize(v); setPage(1); }}
            total={total}
            language={language}
          />

          {photos.length === 0 ? (
            <div className="text-center py-12">
              <Camera className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
              <p className="text-muted-foreground">{t.noPhotos}</p>
            </div>
          ) : (
            <>
              {/* Top pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 mb-4">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                    {t.prev}
                  </Button>
                  <span className="flex items-center text-sm text-muted-foreground px-3">{page} / {totalPages}</span>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                    {t.next}
                  </Button>
                </div>
              )}

              <SportPhotoGrid
                photos={photos}
                viewMode={viewMode}
                favoriteIds={favoriteIds}
                onToggleFavorite={toggleFavorite}
                onPhotoClick={(idx) => { setCurrentPhotoIndex(idx); setLightboxOpen(true); }}
                t={t}
              />

              {totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-8">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                    {t.prev}
                  </Button>
                  <span className="flex items-center text-sm text-muted-foreground px-3">{page} / {totalPages}</span>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                    {t.next}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && photos.length > 0 && (
          <SportLightbox
            photos={photos}
            currentIndex={currentPhotoIndex}
            onIndexChange={setCurrentPhotoIndex}
            onClose={() => setLightboxOpen(false)}
            favoriteIds={favoriteIds}
            onToggleFavorite={toggleFavorite}
            language={language}
            t={t}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
